// src/pages/KarigarManagement/Job/components/KarigarSelector.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useJobManagement, useKarigarManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Badge } from "@/components/ui-v2/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui-v2/dialog";
import { ScrollArea } from "@/components/ui-v2/scroll-area";
import {
  Search,
  Users,
  User,
  Star,
  Clock,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  RefreshCw,
  Loader2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Karigar card component
 */
const KarigarCard = ({ karigar, isSelected, isProductKarigar, onSelect }) => {
  const isAvailable =
    karigar.availability?.isAvailable !== false &&
    karigar.status !== "inactive";

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all",
        isSelected
          ? "border-primary ring-2 ring-primary"
          : isAvailable
          ? "hover:border-primary/50"
          : "opacity-60"
      )}
      onClick={() => {
        if (isAvailable) {
          onSelect(karigar);
        }
      }}
    >
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-lg font-medium">
                {karigar.name?.charAt(0)?.toUpperCase() || "K"}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{karigar.name}</p>
                {isProductKarigar && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">
                    <Sparkles className="h-3 w-3 mr-1" />
                    {karigar.productAssociation?.role || karigar.role || "CAPABLE"}
                  </Badge>
                )}
                {isSelected && <CheckCircle className="h-4 w-4 text-primary" />}
              </div>
              <p className="text-sm text-muted-foreground">
                {karigar.karigarCode}
              </p>
            </div>
          </div>
          {karigar.availabilityScore !== undefined && (
            <div className="text-right">
              <div className="text-2xl font-bold text-primary">
                {karigar.availabilityScore}
              </div>
              <p className="text-xs text-muted-foreground">Score</p>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Star className="h-4 w-4 text-yellow-500" />
              <span className="font-medium">
                {karigar.performance?.overallRating?.toFixed(1) ||
                  karigar.rating?.toFixed(1) ||
                  "0.0"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Rating</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <User className="h-4 w-4 text-blue-500" />
              <span className="font-medium">
                {karigar.workload?.currentJobs ?? karigar.activeJobs ?? 0}/
                {karigar.workload?.maxJobs ?? karigar.maxJobs ?? 5}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Jobs</p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1">
              <Clock className="h-4 w-4 text-purple-500" />
              <span className="font-medium">
                {karigar.estimation?.estimatedDays || "-"}d
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Est. Time</p>
          </div>
        </div>

        {!isAvailable && (
          <div className="mt-3 p-2 bg-red-50 rounded text-sm text-red-600 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            {karigar.status === "inactive" ? "Inactive" : "At full capacity"}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Karigar selection dialog
 */
const KarigarSelectionDialog = ({
  open,
  onOpenChange,
  selectedKarigar,
  onSelect,
  jobType,
  productType,
  metalType,
  expectedDate,
  suggestedKarigars = [],
  productId = null,
}) => {
  const {
    karigars,
    loading: karigarLoading,
    getKarigars,
  } = useKarigarManagement();
  const {
    productKarigars,
    otherKarigars,
    allKarigars,
    getAvailableKarigars: getJobAvailableKarigars,
    loading: jobLoading,
  } = useJobManagement();

  const [search, setSearch] = useState("");
  const [localSelected, setLocalSelected] = useState(selectedKarigar);
  const [fetchingKarigars, setFetchingKarigars] = useState(false);

  useEffect(() => {
    if (open) {
      fetchKarigars();
      setLocalSelected(selectedKarigar);
    }
    return () => {
      if (!open) setSearch("");
    };
  }, [open, selectedKarigar]);

  const fetchKarigars = async () => {
    setFetchingKarigars(true);
    try {
      const requestData = {
        productType: productType || null,
        workType: jobType || null,
        targetDate: expectedDate || null,
        includeOtherKarigars: true,
        productId: productId || null,
      };
      await getJobAvailableKarigars(requestData);
      await getKarigars({ status: "active", limit: 100 });
    } catch (error) {
      console.error("Error fetching karigars:", error);
    } finally {
      setFetchingKarigars(false);
    }
  };

  const filterKarigars = (list) => {
    if (!list || !Array.isArray(list)) return [];
    if (!search) return list;
    const searchLower = search.toLowerCase();
    return list.filter(
      (k) =>
        k.name?.toLowerCase().includes(searchLower) ||
        k.karigarCode?.toLowerCase().includes(searchLower) ||
        k.phone?.includes(search)
    );
  };

  // Merge suggested karigars from product with API results
  const mergedProductKarigars = useMemo(() => {
    const apiKarigars = productKarigars || [];
    const suggested = suggestedKarigars || [];

    const karigarMap = new Map();

    // Add suggested karigars first (they have priority)
    suggested.forEach((k) => {
      const id = k.karigarId || k._id;
      if (id) {
        karigarMap.set(id.toString(), {
          ...k,
          _id: id,
          karigarId: id,
          productAssociation: { role: k.role || "CAPABLE" },
          isFromProduct: true,
        });
      }
    });

    // Add API karigars
    apiKarigars.forEach((k) => {
      const id = k.karigarId || k._id;
      if (id && !karigarMap.has(id.toString())) {
        karigarMap.set(id.toString(), k);
      }
    });

    return Array.from(karigarMap.values());
  }, [productKarigars, suggestedKarigars]);

  const filteredProductKarigars = filterKarigars(mergedProductKarigars);
  const filteredOtherKarigars = filterKarigars(otherKarigars);
  const allKarigarsToUse = allKarigars?.length > 0 ? allKarigars : karigars;
  const filteredAllKarigars = filterKarigars(allKarigarsToUse);

  const isLoading =
    karigarLoading?.list || jobLoading?.availableKarigars || fetchingKarigars;

  const handleSelect = (karigar) => {
    const normalized = {
      _id: karigar._id || karigar.karigarId,
      karigarId: karigar.karigarId || karigar._id,
      name: karigar.name,
      karigarCode: karigar.karigarCode,
      phone: karigar.phone,
      rating: karigar.performance?.overallRating || karigar.rating,
      isFromProduct: karigar.isFromProduct || false,
    };
    setLocalSelected(normalized);
  };

  const handleConfirm = () => {
    onSelect(localSelected);
    onOpenChange(false);
  };

  const isKarigarSelected = (karigar) => {
    return (
      localSelected?.karigarId === karigar.karigarId ||
      localSelected?._id === karigar._id ||
      localSelected?.karigarId === karigar._id ||
      localSelected?._id === karigar.karigarId
    );
  };

  const isProductKarigar = (karigar) => {
    return mergedProductKarigars?.some(
      (pk) => pk.karigarId === karigar.karigarId || pk._id === karigar._id
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Select Karigar
          </DialogTitle>
          <DialogDescription>
            Choose a karigar to assign this job to
            {suggestedKarigars.length > 0 && (
              <span className="ml-2 text-amber-600">
                • {suggestedKarigars.length} suggested from product
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, code, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={fetchKarigars}
            disabled={isLoading}
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
        </div>

        <ScrollArea className="flex-1 pr-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-muted-foreground mt-2">Loading karigars...</p>
            </div>
          ) : (
            <div className="space-y-6 pb-4">
              {/* Product Suggested Karigars */}
              {filteredProductKarigars.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="h-5 w-5 text-amber-500" />
                    <h3 className="text-base font-semibold">
                      {suggestedKarigars.length > 0
                        ? "Product Karigars"
                        : "Recommended Karigars"}
                    </h3>
                    <Badge variant="secondary">
                      {filteredProductKarigars.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredProductKarigars.map((karigar) => (
                      <KarigarCard
                        key={karigar.karigarId || karigar._id}
                        karigar={karigar}
                        isSelected={isKarigarSelected(karigar)}
                        isProductKarigar={true}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {filteredOtherKarigars.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-blue-500" />
                    <h3 className="text-base font-semibold">Other Karigars</h3>
                    <Badge variant="secondary">
                      {filteredOtherKarigars.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredOtherKarigars.map((karigar) => (
                      <KarigarCard
                        key={karigar.karigarId || karigar._id}
                        karigar={karigar}
                        isSelected={isKarigarSelected(karigar)}
                        isProductKarigar={false}
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </div>
              )}

              {filteredProductKarigars.length === 0 &&
                filteredOtherKarigars.length === 0 &&
                filteredAllKarigars.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Users className="h-5 w-5 text-blue-500" />
                      <h3 className="text-base font-semibold">
                        Available Karigars
                      </h3>
                      <Badge variant="secondary">
                        {filteredAllKarigars.length}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {filteredAllKarigars.map((karigar) => (
                        <KarigarCard
                          key={karigar.karigarId || karigar._id}
                          karigar={karigar}
                          isSelected={isKarigarSelected(karigar)}
                          isProductKarigar={isProductKarigar(karigar)}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {filteredProductKarigars.length === 0 &&
                filteredOtherKarigars.length === 0 &&
                filteredAllKarigars.length === 0 && (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-medium">No karigars found</p>
                    <Button
                      variant="outline"
                      className="mt-4"
                      onClick={fetchKarigars}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                )}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="flex-col sm:flex-row gap-4 border-t pt-4">
          {localSelected ? (
            <div className="flex-1 flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-medium">
                {localSelected.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{localSelected.name}</p>
                  {localSelected.isFromProduct && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">
                      From Product
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {localSelected.karigarCode}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLocalSelected(null)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="flex-1 text-sm text-muted-foreground">
              Select a karigar to assign
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={!localSelected}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Confirm
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Karigar selector card component (used in Step 5)
 */
const KarigarSelectorCard = ({
  selectedKarigar,
  onClear,
  onOpenDialog,
  suggestedKarigars = [],
}) => {
  const hasSuggestions = suggestedKarigars.length > 0;

  return (
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Assign Karigar (Optional)
          {hasSuggestions && (
            <Badge className="bg-amber-100 text-amber-800 text-xs ml-2">
              <Sparkles className="h-3 w-3 mr-1" />
              {suggestedKarigars.length} suggested from product
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Assign a karigar now or do it later
          {hasSuggestions && (
            <span className="text-amber-600 ml-1">
              - Product has {suggestedKarigars.length} capable karigar(s)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {selectedKarigar ? (
          <div
            className={cn(
              "flex items-center justify-between p-4 border rounded-lg",
              selectedKarigar.isFromProduct
                ? "bg-amber-50 border-amber-200"
                : "bg-primary/5 border-primary/20"
            )}
          >
            <div className="flex items-center gap-4">
              <div
                className={cn(
                  "h-14 w-14 rounded-full flex items-center justify-center text-xl font-semibold",
                  selectedKarigar.isFromProduct
                    ? "bg-amber-100 text-amber-700"
                    : "bg-primary text-primary-foreground"
                )}
              >
                {selectedKarigar.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-lg">{selectedKarigar.name}</p>
                  {selectedKarigar.isFromProduct && (
                    <Badge className="bg-amber-100 text-amber-800 text-xs">
                      From Product
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {selectedKarigar.karigarCode}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onOpenDialog}
              >
                Change
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClear}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className={cn(
              "w-full h-24 border-dashed",
              hasSuggestions &&
                "border-amber-300 hover:border-amber-400 hover:bg-amber-50"
            )}
            onClick={onOpenDialog}
          >
            <div className="flex flex-col items-center gap-2">
              <Users
                className={cn(
                  "h-8 w-8",
                  hasSuggestions ? "text-amber-500" : "text-muted-foreground"
                )}
              />
              <span className={hasSuggestions ? "text-amber-700" : ""}>
                {hasSuggestions
                  ? `Select from ${suggestedKarigars.length} Suggested Karigar(s)`
                  : "Click to Select Karigar"}
              </span>
            </div>
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export { KarigarCard, KarigarSelectionDialog, KarigarSelectorCard };
export default KarigarSelectorCard;
